import React, { useEffect, useState } from 'react';
import { Button, Card, Row, Col, Typography, Modal, message, Form, Input, Space, Divider, Switch } from 'antd';
import { EditOutlined, DeleteOutlined, SaveOutlined, ArrowLeftOutlined, LockOutlined } from '@ant-design/icons';
import { useFetchUserById, useUpdateUser, useDeleteUser } from '../../hooks/userHooks';

import UserPermissionEditor from '../../permissions/UserPermissionEditor';
import ChangePasswordModal from './ChangePasswordModal';

const { Title, Text } = Typography;

const SingleUser = ({ userId, onChangeView }) => {
  const { user, loading, error, refetch } = useFetchUserById(userId);
  const { handleUpdate, isUpdating } = useUpdateUser();
  const { handleDelete, isDeleting } = useDeleteUser();

  const [form] = Form.useForm();
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isPasswordModalVisible, setIsPasswordModalVisible] = useState(false);

  // Set up form values when user data is loaded
  useEffect(() => {
    if (user) {
      form.setFieldsValue(user);
    }
  }, [form, user]);


  const startEditing = () => {
    setIsEditing(true);
  };

  const cancelEditing = () => {
    form.setFieldsValue(user);
    setIsEditing(false);
  };

  const saveChanges = async () => {
    try {
      await form.validateFields();
      const values = form.getFieldsValue(true);
      const payload = Object.fromEntries(Object.entries(values).filter(([key]) => ['Name', 'Email', 'IsManager', 'IsDevisManager', 'PermissionSettings', 'PermissionGroupIds'].includes(key) || key.startsWith('CanManage') || ['CanProcessPayments', 'CanProcessReturns', 'TreePermission', 'PreparationPermission', 'ControlePermission'].includes(key)));
      await handleUpdate(userId, payload);
      window.dispatchEvent(new Event('permissions-refresh'));
      window.dispatchEvent(new Event('permission-memberships-changed'));
      message.success('Utilisateur mis à jour avec succès');
      setIsEditing(false);
      refetch();
    } catch (err) {
      message.error('Échec de la mise à jour de l\'utilisateur');
    }
  };

  const confirmDelete = async () => {
    try {
      await handleDelete(userId);
      message.success('Utilisateur supprimé avec succès');
      setIsDeleteModalVisible(false);
      onChangeView('list');
    } catch (err) {
      message.error('Échec de la suppression de l\'utilisateur');
    }
  };

  if (loading) return <p>Chargement...</p>;
  if (error) return <p>Erreur: {error}</p>;
  if (!user) return <p>Aucun utilisateur trouvé</p>;

  const titleStyle = {
    marginBottom: '4px',
    marginTop: '16px'
  };

  return (
    <div className="permission-user-page">
      <Card 
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Détails de l'utilisateur: {user.Name}</span>
            <Space>
              {!isEditing ? (
                <>
                  <Button
                    type="primary"
                    icon={<EditOutlined />}
                    onClick={startEditing}
                  >
                    Modifier
                  </Button>
                  <Button
                    icon={<LockOutlined />}
                    onClick={() => setIsPasswordModalVisible(true)}
                  >
                    Changer le mot de passe
                  </Button>
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => setIsDeleteModalVisible(true)}
                  >
                    Supprimer
                  </Button>
                  <Button
                    icon={<ArrowLeftOutlined />}
                    onClick={() => onChangeView('list')}
                  >
                    Sortir
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={saveChanges}
                    loading={isUpdating}
                  >
                    Enregistrer
                  </Button>
                  <Button onClick={cancelEditing}>
                    Annuler
                  </Button>
                </>
              )}
            </Space>
          </div>
        }
        bordered={false}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={user}
        >
          <Row gutter={24}>
            <Col span={24}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                <Title level={5} style={titleStyle}>Informations Utilisateur</Title>
                <Form.Item name="IsManager" label="Manager" valuePropName="checked" style={{ marginBottom: 0 }}>
                  <Switch disabled={!isEditing} />
                </Form.Item>
              </div>
              <Row gutter={16}>
                <Col xs={24} md={8}>
                  <Form.Item label="Nom" name="Name">
                    <Input disabled={!isEditing} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item label="Email" name="Email">
                    <Input disabled={!isEditing} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item label="ID Utilisateur">
                    <Input value={user.UserID} disabled />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item label="Créé le">
                    <Input value={new Date(user.CreatedAt).toLocaleString()} disabled />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item label="Mis à jour le">
                    <Input value={new Date(user.UpdatedAt).toLocaleString()} disabled />
                  </Form.Item>
                </Col>
              </Row>
            </Col>

            <Col span={24}>
              <Card 
                title={
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Text strong>Permissions</Text>
                    {!isEditing && (
                      <div style={{ 
                        marginLeft: '10px',
                        display: 'flex',
                        alignItems: 'center',
                      }}>
                        <LockOutlined style={{ marginRight: '4px', fontSize: '12px', color: '#999' }} />
                        <Text type="secondary" style={{ fontSize: '12px' }}>Cliquez sur "Modifier" pour changer les permissions</Text>
                      </div>
                    )}
                  </div>
                }
                style={{ marginBottom: 0 }}
                size="small"
                bordered={true}
              >
                <UserPermissionEditor disabled={!isEditing} />
              </Card>
            </Col>

          </Row>
        </Form>
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal
        title="Supprimer l'utilisateur"
        open={isDeleteModalVisible}
        onCancel={() => setIsDeleteModalVisible(false)}
        footer={[
          <Button key="back" onClick={() => setIsDeleteModalVisible(false)}>
            Annuler
          </Button>,
          <Button 
            key="submit" 
            type="primary" 
            danger 
            loading={isDeleting} 
            onClick={confirmDelete}
          >
            Supprimer
          </Button>,
        ]}
      >
        <p>Êtes-vous sûr de vouloir supprimer l'utilisateur <strong>{user.Name}</strong>?</p>
        <p>Cette action ne peut pas être annulée.</p>
      </Modal>

      {/* Change Password Modal */}
      <ChangePasswordModal 
        userId={userId}
        visible={isPasswordModalVisible}
        onCancel={() => setIsPasswordModalVisible(false)}
      />
    </div>
  );
};

export default SingleUser;
